import React, { useEffect, useMemo, useState } from 'react'
import { Dialog } from '@headlessui/react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import toast from 'react-hot-toast'
import { AppDispatch, RootState } from '../../app/store'
import {
  setAddressIdLocally,
  setDeliveryAddressLocally,
} from '../../features/cart/cartSlice'
import { useChangeCartTypeMutation } from '../../features/cart/cartAPI'
import { loadCartItems, selectCartId, selectCartInfo } from '../../features/cart/cartItemsSlice'
import { useGetCustomerAddressesQuery } from '../../features/csFlow/csFlowAPI'
import { buildCompleteAddressFlow } from '../csFlow/components/DeliveryComponents'

interface AddressSelectionModalProps {
  isOpen: boolean
  onClose: () => void
  userId: number
  branchId: number
}

interface AddressOption {
  id: number
  label?: string
  address?: string
  descriptiveAddress?: string
  type?: string
  addressName?: string
}

/**
 * Cashier delivery address popup — same shell + DeliveryComponents as CS
 * (CsOrderTypeSelectionPopup address step).
 */
const AddressSelectionModal: React.FC<AddressSelectionModalProps> = ({
  isOpen,
  onClose,
  userId,
  branchId,
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch<AppDispatch>()
  const isRTL = useSelector((state: RootState) => state.settings.settings.language === 'ar')
  const cartId = useSelector(selectCartId)
  const cartInfo = useSelector(selectCartInfo)
  const cartItems = useSelector((state: RootState) => state.cartItems.cartItems)
  const existingAddressId = useSelector((state: RootState) => state.cart.address_id)

  const [addressStep, setAddressStep] = useState<'noAddresses' | 'savedAddresses' | 'addNewAddress'>('savedAddresses')
  const [selectedAddress, setSelectedAddress] = useState<AddressOption | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [changeCartType] = useChangeCartTypeMutation()

  const {
    data: addressesData,
    isLoading: isLoadingAddresses,
    refetch: refetchAddresses,
  } = useGetCustomerAddressesQuery(
    { userId },
    {
      skip: !isOpen || userId <= 0,
      refetchOnMountOrArgChange: true,
    }
  )

  const savedAddresses = useMemo<AddressOption[]>(() => {
    if (!addressesData?.data) return []

    return addressesData.data.map((address: any) => {
      const descriptiveAddress =
        address.descriptive_address ??
        address.descriptiveAddress ??
        address.description ??
        address.address ??
        ''

      return {
        id: Number(address.id),
        label: address.label ?? address.address_name ?? address.name ?? '',
        address: address.address ?? descriptiveAddress,
        descriptiveAddress,
        type: address.type ?? '',
        addressName: address.address_name ?? address.name ?? address.label ?? '',
      }
    })
  }, [addressesData])

  useEffect(() => {
    if (!isOpen) return

    if (savedAddresses.length === 0) {
      setAddressStep('noAddresses')
      setSelectedAddress(null)
      return
    }

    setAddressStep('savedAddresses')
    const existingId = existingAddressId ? Number(existingAddressId) : null
    const matched = existingId
      ? savedAddresses.find((a) => a.id === existingId) || null
      : null
    setSelectedAddress(matched)
  }, [isOpen, savedAddresses, existingAddressId])

  const persistSelectedAddress = async (address: AddressOption) => {
    setIsSubmitting(true)
    try {
      dispatch(setAddressIdLocally(address.id))
      dispatch(
        setDeliveryAddressLocally({
          id: String(address.id),
          customer_id: userId,
          name: address.label || address.addressName || address.type || '',
          address: address.descriptiveAddress || address.address || '',
          city: '',
          is_default: false,
        })
      )
      localStorage.setItem('selected_address_id', String(address.id))

      const activeCartId = cartId || cartInfo?.id
      if (activeCartId && cartItems.length > 0) {
        await changeCartType({
          cartId: activeCartId,
          typeId: 1,
          branchId,
          userId,
          addressId: address.id,
        }).unwrap()

        await dispatch(loadCartItems({ forceRefresh: true }) as any)
      }

      toast.success(t('cs.order_type_updated') || 'تم اختيار عنوان التوصيل', {
        duration: 2000,
        position: 'top-center',
      })
      onClose()
    } catch (error) {
      toast.error(t('cs.failed_to_update_order_type') || 'فشل تحديث عنوان التوصيل', {
        duration: 3000,
        position: 'top-center',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleAddressSelected = (address: AddressOption) => {
    setSelectedAddress(address)
  }

  const handleDone = async () => {
    if (!selectedAddress) return
    await persistSelectedAddress(selectedAddress)
  }

  const handleHeaderBack = () => {
    if (addressStep === 'addNewAddress') {
      setAddressStep(savedAddresses.length > 0 ? 'savedAddresses' : 'noAddresses')
      return
    }
    onClose()
  }

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      {/* Same backdrop as CS */}
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

      {/* Same modal container as CsOrderTypeSelectionPopup */}
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel
          className="w-[38%] max-w-2xl h-[80vh] bg-white rounded-[25px] flex flex-col overflow-hidden"
          dir={isRTL ? 'rtl' : 'ltr'}
        >
          {/* Header — hidden on addNewAddress (DeliveryComponents renders its own) */}
          {addressStep !== 'addNewAddress' && (
            <div className="px-[18px] pt-[18px] pb-4 flex items-center justify-start gap-2 flex-shrink-0">
              <button
                onClick={handleHeaderBack}
                className="w-10 h-10 bg-white rounded-xl flex items-center justify-center border border-gray-300 transition-colors hover:bg-gray-50"
              >
                {isRTL ? (
                  <ChevronRight className="w-5 h-5 text-gray-600" />
                ) : (
                  <ChevronLeft className="w-5 h-5 text-gray-600" />
                )}
              </button>
              <h2 className="text-lg font-semibold font-['IBM_Plex_Sans_Arabic'] text-gray-900">
                {t('cs.delivery_address') || 'عنوان التوصيل'}
              </h2>
              <div className="w-10" />
            </div>
          )}

          {/* Same address body as CS currentStep === 'address' */}
          <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
            {isSubmitting ? (
              <div className="flex items-center justify-center flex-1">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--brand-primary)]" />
              </div>
            ) : (
              buildCompleteAddressFlow({
                isLoadingAddresses,
                addressStep,
                savedAddresses: savedAddresses as any,
                selectedAddress: selectedAddress as any,
                onAddressSelected: handleAddressSelected as any,
                onAddNewAddress: () => setAddressStep('addNewAddress'),
                onDone: handleDone,
                onAddressSaved: () => {
                  setAddressStep('savedAddresses')
                  refetchAddresses().catch(() => {})
                },
                onBack: () => {
                  if (addressStep === 'addNewAddress') {
                    setAddressStep(savedAddresses.length > 0 ? 'savedAddresses' : 'noAddresses')
                  } else {
                    onClose()
                  }
                },
                userId,
                isRTL,
              })
            )}
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  )
}

export default AddressSelectionModal
